import { RouteComponentProps } from "react-router-dom"
import { useProject } from "../../../projects/hooks/use-project"
import { useGetTasksQuery } from "../../../tasks/api"
import CircularProgress from "@material-ui/core/CircularProgress"
import TaskTreeGrid from "../../../treegrid/task-tree-grid/TaskTreeGrid"
import ErrorList from "../../../components/ErrorList"
import { useProjectRouteContext } from "../hooks/use-project-route-context"
import { useProjectCalendarSettings } from "../../providers/project-calendar-settings"
import { useTranslation } from "react-i18next"

type RouteParams = { projectId: string }

const queryOptions = { refetchOnMountOrArgChange: true } as const

const ProjectGanttRoute = ({ match }: RouteComponentProps<RouteParams>) => {
  const translations = useTranslations()
  const projectId = match.params.projectId
  const {
    project,
    bulkAddTasks,
    isLoading: isLoadingProject,
    isError: isErrorProject,
  } = useProject(projectId, queryOptions)
  const tasksQuery = { project: projectId, archived: "false", links: "true", files: "true" } as const
  const {
    data: tasks = [],
    isLoading: isLoadingTasks,
    isError: isErrorTasks,
  } = useGetTasksQuery(tasksQuery, queryOptions)
  const context = useProjectRouteContext()
  const { calendarSettings } = useProjectCalendarSettings()

  if (isLoadingProject || isLoadingTasks) return <CircularProgress />
  if (isErrorProject) return <ErrorList errors={[translations.fetchErrorProject]} />
  if (!project) return <ErrorList errors={[translations.projectNotFoundError]} />
  if (isErrorTasks) return <ErrorList errors={[translations.fetchErrorTasks]} />

  return (
    <TaskTreeGrid
      project={project}
      tasks={tasks}
      calendarSettings={calendarSettings}
      onAddTask={bulkAddTasks}
      showActualBar={context?.isActualBarShown}
      showBaselineBar={context?.isBaselineBarShown}
      isFullScreen={context?.isFullScreen}
      showGantt
    />
  )
}

const defaultTranslations = {
  projectNotFoundError: "Project not found",
  fetchErrorProject: "Failed to fetch project",
  fetchErrorTasks: "Failed to fetch tasks",
  fetchErrorHolidays: "Failed to fetch holidays",
}

const useTranslations = (): typeof defaultTranslations => {
  const { t } = useTranslation("translation")

  return {
    projectNotFoundError: t("projectNotFoundError", defaultTranslations.projectNotFoundError),
    fetchErrorProject: t("fetchErrorProject", defaultTranslations.fetchErrorProject),
    fetchErrorTasks: t("fetchErrorTasks", defaultTranslations.fetchErrorTasks),
    fetchErrorHolidays: t("fetchErrorHolidays", defaultTranslations.fetchErrorHolidays),
  }
}

export default ProjectGanttRoute
